import { createContext, FC, useContext } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { AskIda, Enterprise, EnterpriseAppState, EventEntity, FarmEntity } from "@ctra/api";
import { isFulfilled, isPending, isRejected, Nullable, parseQuery } from "@ctra/utils";

export enum CreateAskIdaTrackerURLParams {
  farm = "farm",
  createTrackerKey = "createAskIda",
  origin = "origin"
}

export enum CreateAskIdaOrigin {
  timeline = "timeline"
}

interface BaseContext {
  api: {
    open: (eventID: EventEntity["id"], origin: CreateAskIdaOrigin, farmID?: FarmEntity["id"]) => void;
    close: () => void;
  };
  searchParams: {
    eventID: Nullable<EventEntity["id"]>;
    origin: Nullable<CreateAskIdaOrigin>;
    farmID: Nullable<FarmEntity["id"]>;
  };
  meta: {
    isTrackerCreated: boolean;
    isCreateTrackerPending: boolean;
    isCreateTrackerFailed: boolean;
  };
}

const DefaultContext = createContext<BaseContext>({
  api: {
    // istanbul ignore next
    open: () => void 0,
    // istanbul ignore next
    close: () => void 0
  },
  searchParams: {
    eventID: null,
    origin: null,
    farmID: null
  },
  meta: {
    isTrackerCreated: false,
    isCreateTrackerPending: false,
    isCreateTrackerFailed: false
  }
});

/**
 * Context provider to help creating an Ask Ida tracker
 * @param children
 */
const _CreateAskIdaTrackerDialogContextProvider: FC = ({ children }) => {
  const { search, pathname } = useLocation();
  const searchParams = new URLSearchParams(search);

  const {
    createAskIda: eventID,
    origin,
    farm: farmID
  } = parseQuery(
    searchParams,
    CreateAskIdaTrackerURLParams.createTrackerKey,
    CreateAskIdaTrackerURLParams.origin,
    CreateAskIdaTrackerURLParams.farm
  );

  /**
   * Tell if the tracker is created
   */
  const isTrackerCreated = useSelector<EnterpriseAppState, boolean>((state) =>
    isFulfilled(state, AskIda.types.CREATE_ASK_IDA_TRACKER)
  );

  /**
   * Tell if the tracker is created
   */
  const isCreateTrackerPending = useSelector<EnterpriseAppState, boolean>((state) =>
    isPending(state, AskIda.types.CREATE_ASK_IDA_TRACKER)
  );

  /**
   * Tell if the tracker creating has failed
   */
  const isCreateTrackerFailed = useSelector<EnterpriseAppState, boolean>((state) =>
    isRejected(state, AskIda.types.CREATE_ASK_IDA_TRACKER)
  );

  /**
   * Open the dialog (using the given origin)
   * @param eventID
   * @param origin
   * @param farmID
   */
  const open = (eventID: EventEntity["id"], origin: CreateAskIdaOrigin, farmID?: FarmEntity["id"]) => {
    searchParams.set(CreateAskIdaTrackerURLParams.createTrackerKey, eventID);
    searchParams.set(CreateAskIdaTrackerURLParams.origin, origin);
    if (farmID) searchParams.set(CreateAskIdaTrackerURLParams.farm, farmID.toString());

    Enterprise.history.push({ pathname, search: searchParams.toString() });
  };

  /**
   * Close the dialog
   */
  const close = () => {
    searchParams.delete(CreateAskIdaTrackerURLParams.createTrackerKey);
    searchParams.delete(CreateAskIdaTrackerURLParams.origin);
    searchParams.delete(CreateAskIdaTrackerURLParams.farm);

    Enterprise.history.push({ pathname, search: searchParams.toString() });
  };

  return (
    <DefaultContext.Provider
      value={{
        api: {
          open,
          close
        },
        searchParams: {
          origin: origin as CreateAskIdaOrigin,
          eventID,
          farmID: farmID ? +farmID : null
        },
        meta: {
          isTrackerCreated,
          isCreateTrackerPending,
          isCreateTrackerFailed
        }
      }}
    >
      {children}
    </DefaultContext.Provider>
  );
};

export const CreateAskIdaTrackerDialogContext = {
  Provider: _CreateAskIdaTrackerDialogContextProvider,
  Consumer: DefaultContext.Consumer
};

/**
 * @returns {void}
 */
export const useCreateAskIdaTrackerDialog = (): BaseContext => useContext<BaseContext>(DefaultContext);
